// src/components/Panel.js - Versión modificada con enlace directo a PDFs
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DataTable from 'datatables.net';
import 'datatables.net-responsive';
import '../styles/Panel.css';

// URL base para las peticiones API
const API_BASE_URL = 'https://trabajaconnosotros.roboticminds.ec/api';

function Panel() {
  const [apps, setApps] = useState([]);
  const [usuario, setUsuario] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const tableRef = useRef(null);
  const dataTableRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('No hay token guardado, redirigiendo a login');
      navigate('/login');
      return;
    }

    // Preparar headers para las peticiones
    const headers = { Authorization: `Bearer ${token}` };

    // Función para cargar datos del usuario con mejor manejo de errores
    const fetchUsuario = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/usuario`, { headers });
        console.log('Datos de usuario recibidos:', res.data);
        // Comprobamos que haya un nombre o usamos el email
        setUsuario(res.data.nombre || res.data.email || 'Usuario');
        return true;
      } catch (error) {
        console.error('Error al cargar usuario:', error.response?.data || error.message);

        // Si es un error 401 (no autorizado) o el token expiró, redirigir a login
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          setError('Sesión expirada o inválida');
          navigate('/login');
          return false;
        }

        // Si es otro tipo de error, usamos un valor por defecto
        setUsuario('Usuario');
        return true; // Continuamos para cargar las aplicaciones
      }
    };

    // Función para cargar las postulaciones
    const fetchAplicaciones = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/aplicaciones`, { headers });
        console.log('Aplicaciones recibidas:', res.data.length);
        setApps(res.data);
        return true;
      } catch (error) {
        console.error('Error al cargar aplicaciones:', error.response?.data || error.message);

        // Si es un error 401, redirigir a login
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          setError('Sesión expirada o inválida');
          navigate('/login');
          return false;
        }

        setError('Error al cargar las postulaciones');
        return false;
      } finally {
        setLoading(false);
      }
    };

    // Cargar datos en secuencia
    const loadData = async () => {
      const userOk = await fetchUsuario();
      if (userOk) {
        const appsOk = await fetchAplicaciones();
        if (appsOk) {
          // Inicializar DataTable después de cargar los datos
          setTimeout(() => initDataTable(), 100);
        }
      }
    };

    loadData();

    // Cleanup para destruir la tabla al desmontar
    return () => {
      if (dataTableRef.current) {
        dataTableRef.current.destroy();
        dataTableRef.current = null;
      }
    };
  }, [navigate]);

  // Inicializar DataTable
  const initDataTable = () => {
    if (!tableRef.current) return;

    // Destruir tabla existente si ya existe
    if (dataTableRef.current) {
      dataTableRef.current.destroy();
      dataTableRef.current = null;
    }

    try {
      dataTableRef.current = new DataTable('#tabla-postulaciones', {
        responsive: true,
        language: {
          search: "Buscar:",
          lengthMenu: "Mostrar _MENU_ registros por página",
          zeroRecords: "No se encontraron registros",
          info: "Mostrando _START_ a _END_ de _TOTAL_ registros",
          infoEmpty: "Mostrando 0 a 0 de 0 registros",
          infoFiltered: "(filtrado de _MAX_ registros totales)",
          paginate: {
            first: "Primero",
            previous: "Anterior",
            next: "Siguiente",
            last: "Último"
          }
        }
      });
    } catch (err) {
      console.error("Error al inicializar DataTable:", err);
    }
  };

  const cerrarSesion = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  return (
    <div className="panel-main">
      <header className="panel-header">
        <h1>Panel de Postulaciones</h1>
        <div className="user-info">
          {usuario && <span>Bienvenido, {usuario}</span>}
          <button className="logout-btn" onClick={cerrarSesion}>Cerrar sesión</button>
        </div>
      </header>

      <div className="panel-container">
        <h2>Postulaciones recibidas</h2>
        {loading ? (
          <div className="loading-spinner"><p>Cargando postulaciones...</p></div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : apps.length === 0 ? (
          <p className="no-results">No se encontraron postulaciones.</p>
        ) : (
          <table
            ref={tableRef}
            id="tabla-postulaciones"
            className="display responsive nowrap"
            style={{ width: '100%' }}
          >
            <thead>
              <tr>
                <th>Nombre</th><th>Correo</th><th>Teléfono</th>
                <th>Formación</th><th>Nivel</th><th>Área</th>
                <th>Fecha</th><th>CV</th>
              </tr>
            </thead>
            <tbody>
              {apps.map(app => (
                <tr key={app.id}>
                  <td>{app.nombre_completo}</td>
                  <td>{app.correo}</td>
                  <td>{app.telefono}</td>
                  <td>{app.formacion_academica}</td>
                  <td>{app.nivel_estudios}</td>
                  <td>{app.area_interes}</td>
                  <td>{new Date(app.fecha_aplicacion).toLocaleString()}</td>
                  <td>
                    <a 
                      href={`${API_BASE_URL}/aplicaciones/${app.id}/cv`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="cv-link"
                    >
                      Descargar PDF
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default Panel;